import React, { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  AccountTreeOutlined, AssessmentOutlined, BuildOutlined, CheckRounded, ErrorOutlineRounded,
  FactCheckOutlined, InfoOutlined, RadarOutlined, Visibility, VisibilityOff,
} from '@mui/icons-material';

// Light-only by design: these pages never follow the app's dark theme.
const CSS = `
.fwa-root{min-height:100vh;display:flex;align-items:stretch;background:#F8FAFC;color:#0F172A;color-scheme:light;
  font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;-webkit-font-smoothing:antialiased}
.fwa-root *{box-sizing:border-box}
.fwa-aside{flex:0 0 46%;max-width:620px;background:#FFFFFF;border-right:1px solid #E2E8F0;
  display:flex;flex-direction:column;padding:40px 56px}
.fwa-aside-inner{position:sticky;top:40px;display:flex;flex-direction:column;min-height:calc(100vh - 80px)}
.fwa-main{flex:1;min-height:100vh;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:48px 24px}
.fwa-panel{width:100%}
.fwa-logo-mobile{display:none}
@media (max-width:960px){.fwa-aside{display:none}.fwa-main{padding:32px 18px}.fwa-logo-mobile{display:block;height:44px;width:auto;margin:0 auto 28px}}

.fwa-brand img{height:46px;width:auto;display:block}
.fwa-aside h2{margin:56px 0 12px;color:#0F172A;font-size:32px;line-height:1.2;font-weight:700;letter-spacing:-.025em;max-width:480px}
.fwa-aside p.fwa-lede{margin:0;font-size:15.5px;line-height:1.65;color:#64748B;max-width:460px}
.fwa-feats{list-style:none;margin:40px 0 0;padding:0;display:grid;gap:22px;max-width:480px}
.fwa-feats li{display:flex;gap:14px;align-items:flex-start}
.fwa-feat-ic{width:40px;height:40px;border-radius:10px;display:flex;align-items:center;justify-content:center;flex-shrink:0}
.fwa-feat-ic svg{font-size:21px}
.fwa-feats b{display:block;color:#0F172A;font-size:15px;font-weight:600;margin-bottom:3px}
.fwa-feats span{font-size:13.5px;color:#64748B;line-height:1.5}
.fwa-aside-foot{margin-top:auto;padding-top:40px;font-size:12.5px;color:#94A3B8;display:flex;gap:16px}
.fwa-aside-foot a{color:#64748B;text-decoration:none}.fwa-aside-foot a:hover{color:#0F172A}

.fwa-title{margin:0;font-size:28px;font-weight:700;letter-spacing:-.025em;color:#0F172A}
.fwa-sub{margin:8px 0 28px;font-size:15px;color:#64748B;line-height:1.55}
.fwa-sub b{color:#0F172A;font-weight:600}

.fwa-field{margin-bottom:18px}
.fwa-label-row{display:flex;justify-content:space-between;align-items:baseline;margin-bottom:7px}
.fwa-label{font-size:13.5px;font-weight:600;color:#334155}
.fwa-input-wrap{position:relative}
.fwa-input-icon{position:absolute;left:13px;top:50%;transform:translateY(-50%);color:#94A3B8;display:flex;pointer-events:none}
.fwa-input-icon svg{font-size:19px}
.fwa-input{width:100%;height:46px;border:1px solid #D9E0E8;border-radius:10px;background:#fff;padding:0 14px 0 41px;
  font-size:15px;color:#0F172A;font-family:inherit;transition:border-color .15s,box-shadow .15s}
.fwa-input.fwa-noicon{padding-left:14px}
.fwa-input.fwa-hastrail{padding-right:44px}
.fwa-input::placeholder{color:#A3AEBD}
.fwa-input:hover{border-color:#B8C3D1}
.fwa-input:focus{outline:none;border-color:#185FA5;box-shadow:0 0 0 3px rgba(24,95,165,.14)}
.fwa-input:-webkit-autofill{-webkit-box-shadow:0 0 0 100px #fff inset;-webkit-text-fill-color:#0F172A}
select.fwa-input{appearance:none;cursor:pointer;padding-right:36px;
  background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8'%3E%3Cpath d='M1 1.5l5 5 5-5' stroke='%2394A3B8' stroke-width='1.6' fill='none'/%3E%3C/svg%3E");
  background-repeat:no-repeat;background-position:right 14px center}
select.fwa-input.is-empty{color:#A3AEBD}
select.fwa-input option{color:#0F172A}
.fwa-input-trail{position:absolute;right:6px;top:50%;transform:translateY(-50%);display:flex}
.fwa-eye{border:0;background:transparent;color:#94A3B8;cursor:pointer;padding:7px;border-radius:7px;display:flex}
.fwa-eye:hover{color:#475569;background:#F1F5F9}
.fwa-eye svg{font-size:19px}
.fwa-hint{margin-top:6px;font-size:12.5px;line-height:1.45;color:#64748B}
.fwa-hint-ok{color:#15803D}.fwa-hint-warn{color:#B45309}

.fwa-grid{display:grid;grid-template-columns:1fr 1fr;gap:0 14px}
@media (max-width:560px){.fwa-grid{grid-template-columns:1fr}}

.fwa-btn{width:100%;height:48px;border:0;border-radius:10px;background:#185FA5;color:#fff;font-size:15px;font-weight:600;
  font-family:inherit;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:8px;transition:background .15s}
.fwa-btn:hover{background:#0C447C}
.fwa-btn:focus-visible{outline:none;box-shadow:0 0 0 3px rgba(24,95,165,.3)}
.fwa-btn:disabled{background:#CBD5E1;cursor:not-allowed}
.fwa-btn-ghost{width:100%;height:42px;margin-top:8px;border:0;border-radius:10px;background:transparent;color:#64748B;
  font-size:14px;font-weight:500;font-family:inherit;cursor:pointer}
.fwa-btn-ghost:hover{background:#EEF2F6;color:#0F172A}
.fwa-link{color:#185FA5;font-weight:600;text-decoration:none;background:none;border:0;padding:0;cursor:pointer;font:inherit;font-size:inherit}
.fwa-link:hover{text-decoration:underline}
.fwa-link:disabled{color:#94A3B8;cursor:not-allowed;text-decoration:none}
.fwa-foot{margin-top:28px;text-align:center;font-size:14px;color:#64748B}

.fwa-alert{display:flex;gap:10px;align-items:flex-start;padding:11px 13px;border-radius:10px;font-size:13.5px;line-height:1.5;margin-bottom:20px}
.fwa-alert svg{font-size:19px;flex-shrink:0;margin-top:1px}
.fwa-alert-error{background:#FEF2F2;color:#991B1B;border:1px solid #FECACA}
.fwa-alert-success{background:#F0FDF4;color:#166534;border:1px solid #BBF7D0}
.fwa-alert-info{background:#fff;color:#334155;border:1px solid #E2E8F0}

.fwa-spin{width:20px;height:20px;border-radius:50%;border:2.5px solid rgba(255,255,255,.35);border-top-color:#fff;animation:fwa-spin .7s linear infinite}
.fwa-spin-dark{border-color:#E2E8F0;border-top-color:#185FA5;width:30px;height:30px;border-width:3px}
@keyframes fwa-spin{to{transform:rotate(360deg)}}

.fwa-steps{display:flex;align-items:center;margin:0 0 26px}
.fwa-step{display:flex;align-items:center;gap:8px;font-size:12.5px;font-weight:600;color:#94A3B8;white-space:nowrap}
.fwa-step-dot{width:24px;height:24px;border-radius:50%;border:1.5px solid #CBD5E1;display:flex;align-items:center;justify-content:center;font-size:12px;background:#fff}
.fwa-step.is-active{color:#0F172A}.fwa-step.is-active .fwa-step-dot{border-color:#185FA5;background:#185FA5;color:#fff}
.fwa-step.is-done{color:#334155}.fwa-step.is-done .fwa-step-dot{border-color:#15803D;background:#15803D;color:#fff}
.fwa-step-dot svg{font-size:15px}
.fwa-step-line{flex:1;height:1.5px;background:#E2E8F0;margin:0 10px;min-width:16px}
.fwa-step-line.is-done{background:#15803D}

.fwa-otp-row{display:flex;gap:10px;justify-content:center;margin:6px 0 20px}
.fwa-otp{width:52px;height:60px;border:1px solid #D9E0E8;border-radius:12px;text-align:center;font-size:24px;font-weight:600;
  color:#0F172A;font-family:inherit;background:#fff;transition:border-color .15s,box-shadow .15s}
.fwa-otp:focus{outline:none;border-color:#185FA5;box-shadow:0 0 0 3px rgba(24,95,165,.14)}
.fwa-otp.is-filled{border-color:#94A3B8}
@media (max-width:420px){.fwa-otp{width:44px;height:54px;font-size:21px}.fwa-otp-row{gap:7px}}

.fwa-meter{display:flex;gap:4px;margin-top:8px}
.fwa-meter i{flex:1;height:4px;border-radius:2px;background:#E2E8F0}

.fwa-overlay{position:fixed;inset:0;z-index:1500;background:rgba(15,23,42,.55);display:flex;align-items:center;justify-content:center;padding:20px}
.fwa-modal{width:100%;max-width:440px;background:#fff;border-radius:16px;padding:28px;color:#0F172A;font-family:'Inter',-apple-system,sans-serif}
.fwa-modal h3{margin:0 0 6px;font-size:20px;font-weight:700;letter-spacing:-.02em}
.fwa-modal p{margin:0 0 20px;font-size:14px;color:#64748B;line-height:1.55}
.fwa-check{display:flex;gap:11px;align-items:flex-start;padding:12px 14px;border:1px solid #E2E8F0;border-radius:10px;margin-bottom:10px;cursor:pointer;font-size:14px;line-height:1.5;color:#334155}
.fwa-check:hover{border-color:#CBD5E1}
.fwa-check input{width:17px;height:17px;margin:2px 0 0;accent-color:#185FA5;flex-shrink:0;cursor:pointer}
.fwa-check a{color:#185FA5;font-weight:600;text-decoration:none}.fwa-check a:hover{text-decoration:underline}

.fwa-done{text-align:center;padding:12px 0 4px}
.fwa-done-ic{width:64px;height:64px;border-radius:50%;background:#F0FDF4;color:#15803D;display:flex;align-items:center;justify-content:center;margin:0 auto 18px}
.fwa-done-ic svg{font-size:36px}
`;

const FEATURES = [
  { icon: <RadarOutlined />, fg: '#0C447C', bg: '#E6F1FB', title: 'Continuous vendor scanning',
    text: 'Hundreds of security checks on every vendor, on your schedule.' },
  { icon: <FactCheckOutlined />, fg: '#27500A', bg: '#EAF3DE', title: 'Questionnaires',
    text: 'ISO 27001, GDPR, HIPAA, PCI DSS and more, sent and reviewed in one place.' },
  { icon: <BuildOutlined />, fg: '#633806', bg: '#FAEEDA', title: 'Remediation tracking',
    text: 'Turn findings into fix requests and track them to done.' },
  { icon: <AccountTreeOutlined />, fg: '#3C3489', bg: '#EEEDFE', title: 'Fourth-party visibility',
    text: 'See the services your vendors depend on.' },
  { icon: <AssessmentOutlined />, fg: '#791F1F', bg: '#FCEBEB', title: 'Board-ready reports',
    text: 'Security, portfolio and board summary reports in one click.' },
];

export default function AuthLayout({ width = 420, children }: { variant?: 'login' | 'signup'; width?: number; children: ReactNode }) {
  return (
    <div className="fwa-root">
      <style>{CSS}</style>
      <aside className="fwa-aside">
        <div className="fwa-aside-inner">
          <div className="fwa-brand"><img src="/logo.png" alt="FluxWatch" /></div>
          <h2>Know your vendor risk before it becomes your incident.</h2>
          <p className="fwa-lede">The agentic third-party risk platform for teams that can't wait for annual reviews.</p>
          <ul className="fwa-feats">
            {FEATURES.map(f => (
              <li key={f.title}>
                <span className="fwa-feat-ic" style={{ color: f.fg, background: f.bg }}>{f.icon}</span>
                <div><b>{f.title}</b><span>{f.text}</span></div>
              </li>
            ))}
          </ul>
          <div className="fwa-aside-foot">
            <span>© {new Date().getFullYear()} Fluxvell</span>
            <RouterLink to="/terms">Terms</RouterLink>
            <RouterLink to="/privacy">Privacy</RouterLink>
          </div>
        </div>
      </aside>

      <main className="fwa-main">
        <div className="fwa-panel" style={{ maxWidth: width }}>
          <img className="fwa-logo-mobile" src="/logo.png" alt="FluxWatch" />
          {children}
        </div>
      </main>
    </div>
  );
}

type FieldProps = React.InputHTMLAttributes<HTMLInputElement> & {
  id: string;
  label: string;
  icon?: ReactNode;
  trailing?: ReactNode;
  labelRight?: ReactNode;
  hint?: ReactNode;
  hintTone?: 'muted' | 'ok' | 'warn';
  after?: ReactNode;
};

export function AuthField({ id, label, icon, trailing, labelRight, hint, hintTone = 'muted', after, ...input }: FieldProps) {
  return (
    <div className="fwa-field">
      <div className="fwa-label-row">
        <label htmlFor={id} className="fwa-label">{label}</label>
        {labelRight}
      </div>
      <div className="fwa-input-wrap">
        {icon && <span className="fwa-input-icon">{icon}</span>}
        <input id={id} className={`fwa-input${icon ? '' : ' fwa-noicon'}${trailing ? ' fwa-hastrail' : ''}`} {...input} />
        {trailing && <span className="fwa-input-trail">{trailing}</span>}
      </div>
      {after}
      {hint && <div className={`fwa-hint${hintTone === 'muted' ? '' : ` fwa-hint-${hintTone}`}`}>{hint}</div>}
    </div>
  );
}

export function AuthSelect({ id, label, icon, value, onChange, options, placeholder }: {
  id: string; label: string; icon?: ReactNode; value: string; onChange: (v: string) => void;
  options: readonly string[]; placeholder: string;
}) {
  return (
    <div className="fwa-field">
      <div className="fwa-label-row"><label htmlFor={id} className="fwa-label">{label}</label></div>
      <div className="fwa-input-wrap">
        {icon && <span className="fwa-input-icon">{icon}</span>}
        <select id={id} value={value} onChange={e => onChange(e.target.value)}
          className={`fwa-input${icon ? '' : ' fwa-noicon'}${value ? '' : ' is-empty'}`}>
          <option value="" disabled>{placeholder}</option>
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
    </div>
  );
}

export function PasswordToggle({ shown, onToggle }: { shown: boolean; onToggle: () => void }) {
  return (
    <button type="button" className="fwa-eye" onClick={onToggle} aria-label={shown ? 'Hide password' : 'Show password'}>
      {shown ? <VisibilityOff /> : <Visibility />}
    </button>
  );
}

export function AuthAlert({ tone = 'error', children }: { tone?: 'error' | 'success' | 'info'; children: ReactNode }) {
  const icon = tone === 'error' ? <ErrorOutlineRounded /> : tone === 'success' ? <CheckRounded /> : <InfoOutlined />;
  return <div className={`fwa-alert fwa-alert-${tone}`} role={tone === 'error' ? 'alert' : 'status'}>{icon}<div>{children}</div></div>;
}

export function Spinner({ dark = false }: { dark?: boolean }) {
  return <span className={`fwa-spin${dark ? ' fwa-spin-dark' : ''}`} aria-label="Loading" />;
}

export function AuthSteps({ steps, active }: { steps: string[]; active: number }) {
  return (
    <div className="fwa-steps">
      {steps.map((label, i) => (
        <React.Fragment key={label}>
          {i > 0 && <span className={`fwa-step-line${i <= active ? ' is-done' : ''}`} />}
          <span className={`fwa-step${i === active ? ' is-active' : i < active ? ' is-done' : ''}`}>
            <span className="fwa-step-dot">{i < active ? <CheckRounded /> : i + 1}</span>
            {label}
          </span>
        </React.Fragment>
      ))}
    </div>
  );
}
